import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend
} from 'recharts';
import html2canvas from 'html2canvas';
import { historicoAPI, authAPI } from '../services/api';
import { getLabels } from '../utils/userLabels';
import './Historico.css';

// Paleta de colores
const COLORS = {
  primary: '#667eea',
  secondary: '#764ba2',
  green: '#27ae60',
  red: '#e74c3c',
  orange: '#f39c12',
  blue: '#3498db',
  purple: '#9b59b6',
  teal: '#1abc9c',
  pink: '#e91e63',
  indigo: '#3f51b5',
};

const PIE_COLORS = ['#667eea', '#27ae60', '#f39c12', '#e74c3c', '#9b59b6', '#1abc9c', '#3498db', '#e91e63'];

const ETAPA_COLORS = {
  prospecto: '#3498db',
  calificacion: '#f39c12',
  propuesta: '#9b59b6',
  negociacion: '#e67e22',
  ganada: '#27ae60',
  perdida: '#e74c3c',
};

const ETAPA_LABELS = {
  prospecto: 'Prospecto',
  calificacion: 'Calificación',
  propuesta: 'Propuesta',
  negociacion: 'Negociación',
  ganada: 'Ganada',
  perdida: 'Perdida',
};

// Formatea mes "2026-01" → "Ene 26"
function formatMes(mes) {
  if (!mes) return '';
  const [year, month] = mes.split('-');
  const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  return `${meses[parseInt(month, 10) - 1]} ${year.slice(2)}`;
}

// Formatea moneda
function formatCurrency(value) {
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(value);
}

// Custom tooltip
function CustomTooltip({ active, payload, label, formatter }) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="historico-tooltip">
      <div className="tooltip-label">{formatMes(label) || label}</div>
      {payload.map((entry, i) => (
        <div key={i} className="tooltip-item">
          <span className="tooltip-dot" style={{ backgroundColor: entry.color }}></span>
          <span>{entry.name}: <strong>{formatter ? formatter(entry.value) : entry.value}</strong></span>
        </div>
      ))}
    </div>
  );
}

// Wrapper de tarjeta con botón exportar
function ChartCard({ title, icon, fullWidth, children }) {
  const cardRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    if (!cardRef.current || exporting) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true,
      });
      const link = document.createElement('a');
      const fileName = title.replace(/[^a-zA-Z0-9áéíóúñÁÉÍÓÚÑ ]/g, '').trim().replace(/\s+/g, '_');
      link.download = `${fileName}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Error al exportar gráfico:', err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className={`historico-chart-card ${fullWidth ? 'full-width' : ''}`} ref={cardRef}>
      <div className="chart-card-header">
        <h3><span className="chart-icon">{icon}</span> {title}</h3>
        <button
          className="chart-export-btn"
          onClick={handleExport}
          title="Exportar como imagen"
          disabled={exporting}
        >
          {exporting ? (
            <span className="export-spinner"></span>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          )}
        </button>
      </div>
      {children}
    </div>
  );
}

function Historico() {
  const [data, setData] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meses, setMeses] = useState(3);

  const labels = getLabels(user?.tipo_usuario || 'coach');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [historicoRes, authRes] = await Promise.all([
        historicoAPI.getData(meses),
        authAPI.check(),
      ]);
      setData(historicoRes.data);
      if (authRes.data.authenticated) {
        setUser(authRes.data.user);
      }
    } catch (err) {
      setError('Error al cargar los datos del histórico');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [meses]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Feature flags
  const hasFacturacion = user?.facturacion_habilitada === true || user?.facturacion_habilitada === 1;
  const hasOportunidades = user?.oportunidades_habilitadas === true || user?.oportunidades_habilitadas === 1;
  const hasTareas = user?.tareas_habilitadas === true || user?.tareas_habilitadas === 1;

  if (loading) {
    return (
      <div className="historico-page">
        <div className="historico-loading">
          <div className="spinner"></div>
          <div>Cargando histórico...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="historico-page">
        <div className="historico-error">{error}</div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="historico-page">
      {/* Header */}
      <div className="historico-header">
        <h1>📊 Histórico de Actividad</h1>
        <div className="historico-filters">
          <label>Período:</label>
          <select value={meses} onChange={(e) => setMeses(Number(e.target.value))}>
            <option value={3}>Últimos 3 meses</option>
            <option value={6}>Últimos 6 meses</option>
            <option value={12}>Últimos 12 meses</option>
            <option value={24}>Últimos 2 años</option>
          </select>
        </div>
      </div>

      {/* KPIs */}
      <div className="historico-kpis">
        <div className="historico-kpi">
          <div className="historico-kpi-value">{data.totales?.sesiones ?? 0}</div>
          <div className="historico-kpi-label">{labels.sesiones} totales</div>
        </div>
        <div className="historico-kpi">
          <div className="historico-kpi-value">{data.totales?.clientes_activos ?? 0}</div>
          <div className="historico-kpi-label">Clientes activos</div>
        </div>
        <div className="historico-kpi">
          <div className="historico-kpi-value">{data.totales?.horas_totales ?? 0}h</div>
          <div className="historico-kpi-label">Horas completadas</div>
        </div>
        {hasFacturacion && data.facturacion_por_mes && (
          <div className="historico-kpi">
            <div className="historico-kpi-value">
              {formatCurrency(data.facturacion_por_mes.reduce((sum, m) => sum + m.facturado, 0))}
            </div>
            <div className="historico-kpi-label">Facturado ({meses}m)</div>
          </div>
        )}
        {hasFacturacion && data.facturacion_por_mes && (
          <div className="historico-kpi">
            <div className="historico-kpi-value">
              {formatCurrency(data.facturacion_por_mes.reduce((sum, m) => sum + m.cobrado, 0))}
            </div>
            <div className="historico-kpi-label">Cobrado ({meses}m)</div>
          </div>
        )}
        {hasOportunidades && data.oportunidades_por_mes && (
          <div className="historico-kpi">
            <div className="historico-kpi-value">
              {data.oportunidades_por_mes.reduce((sum, m) => sum + m.total, 0)}
            </div>
            <div className="historico-kpi-label">Oportunidades ganadas</div>
          </div>
        )}
      </div>

      {/* =================== ACTIVIDAD =================== */}
      <h2 className="historico-section-title">Actividad</h2>
      <div className="historico-charts">

        {/* Sesiones por mes */}
        <ChartCard title={`${labels.sesiones} por mes`} icon="📈">
          {data.sesiones_por_mes && data.sesiones_por_mes.some(m => m.total > 0) ? (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={data.sesiones_por_mes}>
                <defs>
                  <linearGradient id="gradSesiones" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="mes" tickFormatter={formatMes} tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="total" name={labels.sesiones} stroke={COLORS.primary} fill="url(#gradSesiones)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="historico-empty">
              <div className="empty-icon">📭</div>
              <div>Sin datos en este período</div>
            </div>
          )}
        </ChartCard>

        {/* Citas completadas vs canceladas */}
        <ChartCard title="Citas por mes" icon="📅">
          {data.citas_por_mes && data.citas_por_mes.some(m => m.total > 0) ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.citas_por_mes}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="mes" tickFormatter={formatMes} tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="completadas" name="Completadas" fill={COLORS.green} radius={[3, 3, 0, 0]} />
                <Bar dataKey="canceladas" name="Canceladas" fill={COLORS.red} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="historico-empty">
              <div className="empty-icon">📭</div>
              <div>Sin datos en este período</div>
            </div>
          )}
        </ChartCard>

        {/* Horas completadas por mes */}
        <ChartCard title="Horas completadas por mes" icon="⏱️">
          {data.horas_por_mes && data.horas_por_mes.some(m => m.horas > 0) ? (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={data.horas_por_mes}>
                <defs>
                  <linearGradient id="gradHoras" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.teal} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={COLORS.teal} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="mes" tickFormatter={formatMes} tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip formatter={(v) => `${v}h`} />} />
                <Area type="monotone" dataKey="horas" name="Horas" stroke={COLORS.teal} fill="url(#gradHoras)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="historico-empty">
              <div className="empty-icon">📭</div>
              <div>Sin datos en este período</div>
            </div>
          )}
        </ChartCard>

        {/* Clientes nuevos por mes */}
        <ChartCard title="Clientes nuevos por mes" icon="👥">
          {data.clientes_por_mes && data.clientes_por_mes.some(m => m.total > 0) ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.clientes_por_mes}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="mes" tickFormatter={formatMes} tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="total" name="Nuevos clientes" fill={COLORS.blue} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="historico-empty">
              <div className="empty-icon">📭</div>
              <div>Sin datos en este período</div>
            </div>
          )}
        </ChartCard>

        {/* Sesiones por día de la semana */}
        <ChartCard title="Actividad por día de la semana" icon="📆">
          {data.sesiones_por_dia_semana && data.sesiones_por_dia_semana.some(d => d.total > 0) ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.sesiones_por_dia_semana}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="dia" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="total" name="Citas completadas" fill={COLORS.purple} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="historico-empty">
              <div className="empty-icon">📭</div>
              <div>Sin datos</div>
            </div>
          )}
        </ChartCard>

        {/* Distribución modalidad */}
        <ChartCard title={`Modalidad de ${labels.sesiones_lower}`} icon="🎯">
          {data.distribucion_modalidad && data.distribucion_modalidad.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={data.distribucion_modalidad}
                  dataKey="total"
                  nameKey="modalidad"
                  cx="50%"
                  cy="45%"
                  outerRadius={80}
                  innerRadius={45}
                  paddingAngle={3}
                  label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {data.distribucion_modalidad.map((entry, i) => (
                    <Cell key={i} fill={entry.modalidad === 'Online' ? COLORS.blue : COLORS.green} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [value, name]} />
                <Legend
                  verticalAlign="bottom"
                  height={36}
                  formatter={(value) => <span style={{ color: '#555', fontSize: 13 }}>{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="historico-empty">
              <div className="empty-icon">📭</div>
              <div>Sin datos</div>
            </div>
          )}
        </ChartCard>
      </div>

      {/* =================== TAREAS =================== */}
      {hasTareas && data.tareas_por_mes && (
        <>
          <h2 className="historico-section-title">Tareas</h2>
          <div className="historico-charts">
            <ChartCard title="Tareas completadas por mes" icon="✅" fullWidth>
              {data.tareas_por_mes.some(m => m.total > 0) ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={data.tareas_por_mes}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="mes" tickFormatter={formatMes} tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="total" name="Tareas completadas" fill={COLORS.orange} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="historico-empty">
                  <div className="empty-icon">📭</div>
                  <div>Sin tareas completadas en este período</div>
                </div>
              )}
            </ChartCard>
          </div>
        </>
      )}

      {/* =================== OPORTUNIDADES =================== */}
      {hasOportunidades && data.oportunidades_por_mes && (
        <>
          <h2 className="historico-section-title">Oportunidades</h2>
          <div className="historico-charts">
            {/* Oportunidades ganadas por mes */}
            <ChartCard title="Oportunidades ganadas por mes" icon="🏆">
              {data.oportunidades_por_mes.some(m => m.total > 0) ? (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={data.oportunidades_por_mes}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="mes" tickFormatter={formatMes} tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="left" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip formatter={(v) => typeof v === 'number' && v > 100 ? formatCurrency(v) : v} />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line yAxisId="left" type="monotone" dataKey="total" name="Ganadas" stroke={COLORS.green} strokeWidth={2} dot={{ r: 4 }} />
                    <Line yAxisId="right" type="monotone" dataKey="valor" name="Valor (€)" stroke={COLORS.orange} strokeWidth={2} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="historico-empty">
                  <div className="empty-icon">📭</div>
                  <div>Sin oportunidades ganadas en este período</div>
                </div>
              )}
            </ChartCard>

            {/* Pipeline por etapa */}
            <ChartCard title="Pipeline por etapa" icon="📊">
              {data.distribucion_oportunidades && data.distribucion_oportunidades.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={data.distribucion_oportunidades.map(d => ({
                        ...d,
                        nombre: ETAPA_LABELS[d.etapa] || d.etapa,
                      }))}
                      dataKey="total"
                      nameKey="nombre"
                      cx="50%"
                      cy="45%"
                      outerRadius={80}
                      innerRadius={45}
                      paddingAngle={3}
                      label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {data.distribucion_oportunidades.map((entry, i) => (
                        <Cell key={i} fill={ETAPA_COLORS[entry.etapa] || PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [value, name]} />
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      formatter={(value) => <span style={{ color: '#555', fontSize: 13 }}>{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="historico-empty">
                  <div className="empty-icon">📭</div>
                  <div>Sin oportunidades registradas</div>
                </div>
              )}
            </ChartCard>
          </div>
        </>
      )}

      {/* =================== FACTURACIÓN =================== */}
      {hasFacturacion && data.facturacion_por_mes && (
        <>
          <h2 className="historico-section-title">Facturación</h2>
          <div className="historico-charts">
            {/* Facturado vs Cobrado */}
            <ChartCard title="Facturado vs Cobrado por mes" icon="💰" fullWidth>
              {data.facturacion_por_mes.some(m => m.facturado > 0 || m.cobrado > 0) ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={data.facturacion_por_mes}>
                    <defs>
                      <linearGradient id="gradFacturado" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gradCobrado" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.green} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={COLORS.green} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="mes" tickFormatter={formatMes} tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k€`} tick={{ fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip formatter={formatCurrency} />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Area type="monotone" dataKey="facturado" name="Facturado" stroke={COLORS.primary} fill="url(#gradFacturado)" strokeWidth={2} />
                    <Area type="monotone" dataKey="cobrado" name="Cobrado" stroke={COLORS.green} fill="url(#gradCobrado)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="historico-empty">
                  <div className="empty-icon">📭</div>
                  <div>Sin datos de facturación en este período</div>
                </div>
              )}
            </ChartCard>

            {/* Top clientes por facturación */}
            {data.top_clientes_facturacion && data.top_clientes_facturacion.length > 0 && (
              <ChartCard title="Top clientes por facturación" icon="🏅">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={data.top_clientes_facturacion} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" tickFormatter={(v) => `${(v / 1000).toFixed(v >= 1000 ? 0 : 1)}k€`} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="cliente" width={120} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => formatCurrency(v)} />
                    <Bar dataKey="total_facturado" name="Facturado" fill={COLORS.primary} radius={[0, 3, 3, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            )}

            {/* Top clientes por nº de sesiones */}
            {data.top_clientes_sesiones && data.top_clientes_sesiones.length > 0 && (
              <ChartCard title={`Top clientes por nº de ${labels.sesiones_lower}`} icon="⭐">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={data.top_clientes_sesiones} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="cliente" width={120} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="total_sesiones" name={labels.sesiones} fill={COLORS.teal} radius={[0, 3, 3, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            )}
          </div>
        </>
      )}

      {/* Si no tiene facturación, mostrar top clientes aquí */}
      {!hasFacturacion && data.top_clientes_sesiones && data.top_clientes_sesiones.length > 0 && (
        <>
          <h2 className="historico-section-title">Top Clientes</h2>
          <div className="historico-charts">
            <ChartCard title={`Top clientes por nº de ${labels.sesiones_lower}`} icon="⭐" fullWidth>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.top_clientes_sesiones} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="cliente" width={120} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="total_sesiones" name={labels.sesiones} fill={COLORS.teal} radius={[0, 3, 3, 0]} />
                </BarChart>
                </ResponsiveContainer>
            </ChartCard>
          </div>
        </>
      )}
    </div>
  );
}

export default Historico;
